/**
 * This is used to compute the data that is used
 *   for the histograms.
 * For each variables, it create a distribution from
 *   the reference data and compare it with the data
 *   collected by the sampling strategy.
 */

const d3array = require('d3-array');
const d3select = require('d3-selection');

import histogramFactory from './utils/histogram';
import simpleEvaluator from './utils/simple-filter-evaluator';

export function drawHistogram(svgElementId, errorElementId, referenceHashMap, resampledHashMap,
                              selectedVariable) {

  // Build and populate the histogram.
  const fieldName = selectedVariable.fieldName;
  const groupings = selectedVariable.groupings;
  const binCount = groupings.length;
  const histogram = histogramFactory()
    .binCount(binCount)
    .value((siteDetails, index, values) => {
      return siteDetails.data[fieldName];
    })
    .thresholds((value) => {
      for (let index = 0; index < groupings.length; index++) {
        const grouping = groupings[index];
        if (simpleEvaluator(grouping.filter, value)) {
          return index;
        }
      }
    });
  const referenceDataBins = histogram(referenceHashMap);
  const resampledDataBins = histogram(resampledHashMap);
  const referenceHashMapSize = Object.keys(referenceHashMap).length;
  const resampledHashMapSize = Object.keys(resampledHashMap).length;

  const maxValue = 1;

  const meanError = d3array.sum(groupings, (grouping, index) => {
    return Math.abs(referenceDataBins[index].normalisedCount - resampledDataBins[index].normalisedCount);
  });


  // Render the histogram.
  //
  // |- svg ----------------------------------|
  // |            margin top                  |
  // |        |- canvas -------------|        |
  // |        |        |- chart ---| |        |
  // |        |        |           | |        |
  // | margin | y axis | histogram | | margin |
  // | left   |        |           | | right  |
  // |        |        |-----------| |        |
  // |        |           x axis     |        |
  // |        |----------------------|        |
  // |            margin bottom               |
  // |----------------------------------------|
  const svg = d3select.select("#" + svgElementId);
  svg.selectAll('g').remove();

  // Calculate the height and width to use. Use the parent element if we can't tell from the SVG.
  let _elWidth = svg.property('width').baseVal.value;
  let _elHeight = svg.property('height').baseVal.value;
  let parentElement = document.getElementById(svgElementId).parentElement;
  while (_elWidth === 0 || _elHeight === 0) {
    _elWidth = parentElement.clientWidth;
    _elHeight = parentElement.clientHeight;
    parentElement = parentElement.parentElement;
  }
  svg.attr('viewBox', `0 0 ${_elWidth} ${_elHeight}`);

  // Add a canvas which everything will be added to. Canvas implements margins.
  const margins = {
    top: 10,
    left: 10,
    right: 10,
    bottom: 10
  };
  const _canvasWidth = _elWidth - (margins.left + margins.right);
  const _canvasHeight = _elHeight - (margins.top + margins.bottom);
  const _canvas = svg
    .append('g')
    .attr('class', 'canvas')
    .attr('transform',
      `translate(${margins.left},${margins.top})`);

  // Render the meanError left aligned in the top margin space.
  _canvas
    .append('g').attr('transform', 'translate(0,2)')
    .append('text')
    .text(`Data distribution mean error: ${meanError.toFixed(3)}`);

  // Determine the width of each bin.
  var binWidth = Math.abs(_canvasWidth / binCount);

  // x-axis.
  const xAxisHeight = 20;
  const _xAxisCanvas = _canvas.append('g')
    .attr('class', 'x-axis')
    .attr('transform', `translate(0,${_canvasHeight - xAxisHeight})`);
  _xAxisCanvas.selectAll("text")
    .data(groupings)
    .enter().append("text")
    .text(function(grouping, index) {
      return (grouping.renderings.histogram && grouping.renderings.histogram.title) ? grouping.renderings.histogram.title : 'Untitled';
    })
    .attr("class", "text")
    .attr("x", function(grouping, index) {
      return (index * binWidth) + ((binWidth - this.clientWidth) / 2);
    })
    .attr("y", function(grouping, index) {
      return xAxisHeight;
    });

  // y-axis.
  const yAxisWidth = 0;

  // Draw the chart.
  const _chartHeight = _canvasHeight - xAxisHeight;
  const _chartWidth = _canvasWidth - yAxisWidth;
  const _chartCanvas = _canvas
    .append('g')
    .attr('class', 'chart')
    .attr('transform',
      `translate(${yAxisWidth},0)`);

  // Reference histogram.
  const referenceChartBar = _chartCanvas.append('g')
    .selectAll('bar')
    .data(referenceDataBins)
    .enter().append('g')
    .attr('class', 'bar')
    .attr('transform', function(bin, index, bins) {
      return `translate(${index * binWidth},${_chartHeight - (bin.normalisedCount * _chartHeight / maxValue)})`;
    });
  referenceChartBar
    .append("rect")
    .attr("class", "refBar")
    .attr("height", function(bin, index, bins) {
      return bin.normalisedCount * _chartHeight / maxValue;
    })
    .attr('x', 1)
    .attr("width", (binWidth / 2) - 1);
  referenceChartBar
    .append('text')
    .attr('y', function(bin, index, bins) {
      if (bin.length < (referenceHashMapSize * 0.75)) {
        return -5;
      } else {
        return 15;
      }
    })
    .attr('x', binWidth / 4)
    .attr('text-anchor', 'middle')
    .text(function(bin, index, bins) {
      return bin.length;
    });

  // Resampled histogram.
  const resampledChartBar = _chartCanvas.append('g')
    .selectAll('bar')
    .data(resampledDataBins)
    .enter().append('g')
    .attr('class', 'bar')
    .attr('transform', function(bin, index, bins) {
      return `translate(${(index + 0.5) * binWidth},${_chartHeight - (bin.normalisedCount * _chartHeight / maxValue)})`;
    });
  resampledChartBar
    .append("rect")
    .attr("class", "sampleBar")
    .attr("height", function(bin, index, bins) {
      return bin.normalisedCount * _chartHeight / maxValue;
    })
    .attr('x', 0)
    .attr("width", (binWidth / 2) - 1);
  resampledChartBar
    .append('text')
    .attr('y', function(bin, index, bins) {
      if (bin.length < (resampledHashMapSize * 0.75)) {
        return -5;
      } else {
        return 15;
      }
    })
    .attr('x', binWidth / 4)
    .attr('text-anchor', 'middle')
    .text(function(bin, index, bins) {
      return bin.length;
    });

}



// WEBPACK FOOTER //
// ./src/app/statistics.js